import { MonoBehavior } from './core/monoBehavior';
import { SoundManager } from 'game/core/soundManager';
import {
    FACEBOOK_NEXT_POPUP_SHOW,
    FACEBOOK_SHOW_LIMIT,
    NOTIFICATION_NEXT_POPUP_SHOW,
    Store,
    TICKETS_NEXT_SHOW,
    TICKETS_SHOW_LIMIT,
} from './store/gameStore';
import { GameEvent } from './events/gameEvent';
import { EventName } from './events/eventsName';
import { openFeedbackPopup } from './feedback';
import { GAEvent, GAEventNames } from './analytics/events';
import { isIOS } from './core/display';
import { difficultyLevelData } from 'constants/difficulty';
import { StoriesHandler } from './gameUI/UiHandlers/StoriesHandler';
import { Popups } from 'ui/types/popups';
import { GameUI } from './gameUI/GameUI';

export class WinScreen extends MonoBehavior {
    constructor() {
        super();

        // TODO: Maybe legacy ???
        window.addEventListener('showWinScreen' as any, (e: any) => {
            if (e.detail.moves) Store.movesCount = parseInt(e.detail.moves);
            if (e.detail.time) {
                const time = parseInt(e.detail.time) * 1000;
                Store.startTime = performance.now() - time;
                Store.stopTime = performance.now();
            }
            if (e.detail.wins) {
                Store.wins = parseInt(e.detail.wins);
                Store.gameStorage.setItem('wins', Store.wins);
            }

            this.showWinScreen();
        });
        GameEvent.add(EventName.RESET_GAME, this.hideWinScreen.bind(this));
        GameEvent.add(EventName.SHOW_WIN_AFTER_FEEDBACK, this.displayWinScreen.bind(this));
        GameEvent.add(EventName.CLOSE_MODAL, this.onModalsHide.bind(this));
    }
    onModalsHide(e: { id: string; confirm: boolean }) {
        const { id, confirm } = e;
        if (id === 'push_notifications') {
            if (confirm) {
                Store.blockNotificationOnWin();
                GAEvent(GAEventNames.NOTIFICATION_POPUP_SUBSCRIBE, {
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                    number: Store.countPopupShown('push_notifications'),
                });
                GameUI.showPopup(Popups.KING_POPUP, { textKey: 'thanksSubscribing', isNotification: true });
            } else {
                Store.notificationWinPopupNextShow += NOTIFICATION_NEXT_POPUP_SHOW;
                Store.gameStorage.setItem('notificationWinPopupNextShow', Store.notificationWinPopupNextShow);

                GAEvent(GAEventNames.NOTIFICATION_POPUP_SKIP, {
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                    number: Store.countPopupShown('push_notifications'),
                });
            }
            this.displayWinScreen();
        }
        if (id === Popups.STORIES_USE_TICKETS_POPUP) {
            if (confirm) {
                GAEvent(GAEventNames.STORIES_USE_TICKET_BANNER_OPEN, {
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                    number: Store.countPopupShown(Popups.STORIES_USE_TICKETS_POPUP),
                });
            } else {
                GAEvent(GAEventNames.STORIES_USE_TICKET_BANNER_CLOSE, {
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                    number: Store.countPopupShown(Popups.STORIES_USE_TICKETS_POPUP),
                });
            }
            this.displayWinScreen();
        }
        if (id === 'facebook-banner') {
            if (confirm) {
                Store.blockFacebookWinPopup();

                Store.setWandCounter(Store.wandCounter + 5);
                GAEvent(GAEventNames.FACEBOOK_BANNER_FOLLOW, {
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                    number: Store.countPopupShown('facebook-banner'),
                });

                const url = 'https://www.facebook.com/solitairescom';
                if (isIOS()) window.location.href = url;
                else window.open(url, '_blank');
            } else {
                GAEvent(GAEventNames.FACEBOOK_BANNER_CLOSE, {
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                    number: Store.countPopupShown('facebook-banner'),
                });
            }
            this.displayWinScreen();
        }
    }
    hideWinScreen() {
        GameUI.closePopup(Popups.WIN_POPUP);
    }
    
    displayWinScreen() {
        SoundManager.play('winPopupSFX');

        // Compute data for React WinPopup
        const data: {
            timeFormatted: string;
            moves: number;
            recordPercent?: number;
            bestTimeFormatted?: string;
            bestMoves?: number;
            ticketsWon: number;
            gameClass: string;
        } = {
            timeFormatted: Store.getTimeFormat(Store.time),
            moves: Store.movesCount,
            ticketsWon: this.ticketByDifficulty(),
            gameClass: `win-popup-game-${Store.name}`,
        } as any;

        // Best results and record percentage
        if (Store.gamesWinScore) {
            let moveValue = 0; // worst
            let timeValue = 0; // worst

            data.bestTimeFormatted = Store.getTimeFormat(Store.gamesWinScore.minTime);
            data.bestMoves = Store.gamesWinScore.minMoves;

            if (Store.movesCount <= Store.gamesWinScore.minMoves) {
                moveValue = 1;
                (data as any).isBestMoves = true;
            } else if (Store.movesCount < Store.gamesWinScore.medianMoves) {
                moveValue =
                    0.5 +
                    (0.5 * (Store.gamesWinScore.medianMoves - Store.movesCount)) /
                        (Store.gamesWinScore.medianMoves - Store.gamesWinScore.minMoves);
            } else if (Store.movesCount < Store.gamesWinScore.maxMoves) {
                moveValue =
                    (0.5 * (Store.gamesWinScore.maxMoves - Store.movesCount)) /
                    (Store.gamesWinScore.maxMoves - Store.gamesWinScore.medianMoves);
            }

            if (Store.time <= Store.gamesWinScore.minTime) {
                timeValue = 1;
                (data as any).isBestTime = true;
            } else if (Store.time < Store.gamesWinScore.medianTime) {
                timeValue =
                    0.5 +
                    (0.5 * (Store.gamesWinScore.medianTime - Store.time)) /
                        (Store.gamesWinScore.medianTime - Store.gamesWinScore.minTime);
            } else if (Store.time < Store.gamesWinScore.maxTime) {
                timeValue =
                    (0.5 * (Store.gamesWinScore.maxTime - Store.time)) /
                    (Store.gamesWinScore.maxTime - Store.gamesWinScore.medianTime);
            }

            const totalPercentage = (moveValue + timeValue) / 2;
            data.recordPercent = Math.round(totalPercentage * 1000) / 10;
        }
        GameUI.showPopup(Popups.WIN_POPUP, data);
    }

    ticketByDifficulty() {
        if (Store.difficultyBalanced) return 3;

        switch (difficultyLevelData[Store.difficulty].type) {
            case 'easy':
                return 1;
            case 'medium':
                return 2;
            case 'expert':
                return 4;
            case 'master':
                return 5;

            default:
                return 3;
        }
    }

    async showWinScreen() {
        if (Store.showFeedbackOnWin()) {
            openFeedbackPopup('win');
            return;
        }

        // if (Store.wins - Store.notificationWinPopupNextShow > NOTIFICATION_NEXT_POPUP_SHOW + 1) {
        //     Store.notificationWinPopupNextShow = Store.wins + NOTIFICATION_NEXT_POPUP_SHOW;
        //     Store.gameStorage.setItem('notificationWinPopupNextShow', Store.notificationWinPopupNextShow);
        // }

        if ('Notification' in window) {
            if (
                Store.isMobile &&
                Store.notificationWinPopupNextShow !== -1 &&
                Store.wins >= Store.notificationWinPopupNextShow &&
                Notification.permission === 'default'
            ) {
                
                GameUI.showPopup(Popups.NOTIFICATION_POPUP);

                GAEvent(GAEventNames.NOTIFICATION_POPUP_SHOW, {
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                    number: Store.countPopupShown('push_notifications'),
                });
                return;
            }
        }
        if (
            Store.countPopupShown(Popups.FACEBOOK_POPUP) < FACEBOOK_SHOW_LIMIT &&
            Store.language === 'en' &&
            Store.facebookWinPopupNextShow !== -1 &&
            Store.wins >= Store.facebookWinPopupNextShow
        ) {
            GameUI.showPopup(Popups.FACEBOOK_POPUP);
            GAEvent(GAEventNames.FACEBOOK_BANNER_VIEW, {
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
                number: Store.countPopupShown(Popups.FACEBOOK_POPUP),
            });

            Store.facebookWinPopupNextShow += FACEBOOK_NEXT_POPUP_SHOW;
            Store.gameStorage.setItem('facebookWinPopupNextShow', Store.facebookWinPopupNextShow);
            return;
        }
        // if (await this.showTicketPopup()) return;
        this.displayWinScreen();
    }

    async showTicketPopup() {
        const tickets = await Store.getTicketsAmount();

        if (tickets < TICKETS_SHOW_LIMIT) return false;
        if (Store.storiesNextPopup > Store.wins) return false;

        const stories = StoriesHandler.getAllStories();
        const lastStory = stories[stories.length - 1];

        const lastStoryProgress = parseInt(localStorage.getItem(`stories_story_${stories.length}_progress`) || '0');
        if (lastStory.totalImages === lastStoryProgress) return false;

        GameUI.showPopup(Popups.STORIES_USE_TICKETS_POPUP);
        GAEvent(GAEventNames.STORIES_USE_TICKET_BANNER_VIEW, {
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
            number: Store.countPopupShown(Popups.STORIES_USE_TICKETS_POPUP),
        });
        Store.storiesNextPopup = Store.wins + TICKETS_NEXT_SHOW;
        Store.gameStorage.setItem('storiesNextPopup', Store.storiesNextPopup);
        return true;
    }
}
